import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import AddressLookup from "@/components/AddressLookup";

const empty = {
  label: "",
  kind: "pickup",
  org_id: "",
  type: "",
  rate_plan: "",
  billing_schedule: "",
  name: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  contact_name: "",
  contact_phone: "",
  email: "",
  notes: "",
};

/**
 * Create / edit a saved location (Customer=pickup or Consignee=delivery).
 * Used inside AddressDirectory and (via `initial` prop) from the Job form's
 * inline "Create new" flow.
 */
export default function LocationFormDialog({
  open,
  onOpenChange,
  location = null,   // for edit
  initial = null,    // for prefilling create (e.g. from Job form typed values)
  kind = "pickup",   // default when creating fresh
  onSaved,
}) {
  const isEdit = !!location?.id;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        label: location.label || "",
        kind: location.kind || kind,
        org_id: location.org_id || "",
        type: location.type || "",
        rate_plan: location.rate_plan || "",
        billing_schedule: location.billing_schedule || "",
        name: location.name || "",
        street: location.street || "",
        city: location.city || "",
        state: location.state || "",
        zip: location.zip || "",
        contact_name: location.contact_name || "",
        contact_phone: location.contact_phone || "",
        email: location.email || "",
        notes: location.notes || "",
      });
    } else {
      setForm({ ...empty, kind, ...(initial || {}) });
    }
  }, [open, location, initial, kind, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error("Give this a name"); return; }
    if (!form.street && !form.city) { toast.error("Enter at least a street or city"); return; }
    setBusy(true);
    try {
      const payload = { ...form, label: form.label.trim() };
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/locations/${location.id}`, payload);
        saved = data;
        toast.success("Updated");
      } else {
        const { data } = await api.post("/locations", payload);
        saved = data;
        toast.success("Saved");
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-xl border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {isEdit ? "Edit location" : (form.kind === "pickup" ? "New Customer (Pickup)" : "New Consignee (Delivery)")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="mono-label">Location name *</label>
              <Input required autoFocus value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="sharp mt-1 apex-input"
                placeholder="e.g. Costco DC Delta"
                data-testid="loc-form-label" />
            </div>
            <div>
              <label className="mono-label">Use for</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="sharp mt-1" data-testid="loc-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Customer · Pickup</SelectItem>
                  <SelectItem value="delivery">Consignee · Delivery</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mono-label">Company / site name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sharp mt-1 apex-input" data-testid="loc-form-name" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="mono-label">OrgID</label>
              <Input value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value.toUpperCase() })} className="sharp mt-1 apex-input font-mono uppercase" maxLength={12} placeholder="e.g. CLT05" data-testid="loc-form-orgid" />
            </div>
            <div>
              <label className="mono-label">Type</label>
              <Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="sharp mt-1 apex-input" placeholder="WH / DC / Store" data-testid="loc-form-type" />
            </div>
            <div>
              <label className="mono-label">Rate Plan</label>
              <Input value={form.rate_plan} onChange={(e) => setForm({ ...form, rate_plan: e.target.value })} className="sharp mt-1 apex-input" placeholder="Standard" data-testid="loc-form-rate-plan" />
            </div>
            <div>
              <label className="mono-label">Billing</label>
              <Select value={form.billing_schedule || "__none__"} onValueChange={(v) => setForm({ ...form, billing_schedule: v === "__none__" ? "" : v })}>
                <SelectTrigger className="sharp mt-1" data-testid="loc-form-billing"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="Daily">Daily</SelectItem>
                  <SelectItem value="Weekly">Weekly</SelectItem>
                  <SelectItem value="Semi-Monthly">Semi-Monthly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mono-label">Street</label>
            <AddressLookup
              value={form.street}
              onChange={(v) => setForm({ ...form, street: v })}
              onSelect={(a) => setForm({
                ...form,
                street: a.street || form.street,
                city: a.city || form.city,
                state: a.state || form.state,
                zip: a.zip || form.zip,
              })}
              placeholder="Start typing address or business name…"
              dataTestId="loc-form-street"
            />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2">
              <label className="mono-label">City</label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="sharp mt-1 apex-input" data-testid="loc-form-city" />
            </div>
            <div>
              <label className="mono-label">State / Prov</label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="sharp mt-1 apex-input font-mono uppercase" maxLength={3} data-testid="loc-form-state" />
            </div>
            <div>
              <label className="mono-label">Zip / Postal</label>
              <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="sharp mt-1 apex-input font-mono" data-testid="loc-form-zip" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mono-label">Contact name</label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="sharp mt-1 apex-input" />
            </div>
            <div>
              <label className="mono-label">Contact phone</label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="sharp mt-1 apex-input" />
            </div>
            <div>
              <label className="mono-label">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="sharp mt-1 apex-input" data-testid="loc-form-email" />
            </div>
          </div>
          <div>
            <label className="mono-label">Notes</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sharp mt-1 apex-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="sharp border-zinc-300" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="loc-form-submit">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
